import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { extractErrorMessage } from '../../lib/errors';
import AdminLayout from '../../components/layout/AdminLayout';
import ComplianceIndicator from '../../components/ComplianceIndicator';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { POSITION_OPTIONS } from '../../lib/positions';
import {
  Users, UserPlus, Mail, Trash2, CheckCircle, XCircle, Copy, Link as LinkIcon,
  Shield, Hash, ChevronDown, ChevronUp, Search, Download, Eye, MoreVertical,
  Calendar, Clock, FileText, KeyRound, ShieldCheck, ShieldOff, RefreshCw, Briefcase,
  UserCog, ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

const FRONTEND_URL = window.location.origin;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Small presentational helpers ----------
function KpiCard({ title, value, icon: Icon, tone = 'neutral', testId }) {
  const styles = {
    neutral: 'text-foreground bg-muted/60',
    blue: 'text-blue-500 bg-blue-500/10',
    green: 'text-green-500 bg-green-500/10',
    primary: 'text-primary bg-primary/10',
    amber: 'text-amber-500 bg-amber-500/10',
    red: 'text-red-500 bg-red-500/10',
  };
  return (
    <Card className="bg-card border-border rounded-sm" data-testid={testId}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2">
          <div className={cn('p-1.5 sm:p-2 rounded-sm flex-shrink-0', styles[tone])}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
          </div>
          <span className="font-mono text-xl sm:text-2xl font-medium text-foreground">{value}</span>
        </div>
        <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mt-2 sm:mt-3 leading-tight">
          {title}
        </p>
      </CardContent>
    </Card>
  );
}

function FilterChip({ label, count, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border transition-colors whitespace-nowrap',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-accent'
      )}
    >
      {label}
      {typeof count === 'number' && (
        <span className={cn('ml-1.5 font-mono', active ? 'text-primary-foreground/80' : 'text-foreground/60')}>
          {count}
        </span>
      )}
    </button>
  );
}

function initialsOf(name) {
  return (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('');
}

// ---------- Main page ----------
export default function EmployeesPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isMainAdmin = currentUser?.role === 'admin';
  const [employees, setEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitesOpen, setInvitesOpen] = useState(false); // collapsed by default

  const [inviteOpen, setInviteOpen] = useState(false);
  const [supervisorInviteOpen, setSupervisorInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', position: '' });
  const [supervisorInviteForm, setSupervisorInviteForm] = useState({ email: '', name: '', position: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteLinkDialog, setInviteLinkDialog] = useState({ open: false, link: '', name: '' });

  const [deleteDialog, setDeleteDialog] = useState({ open: false, member: null });
  const [roleDialog, setRoleDialog] = useState({ open: false, member: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [letterFilter, setLetterFilter] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [empRes, inviteRes, supRes] = await Promise.all([
        axios.get(`${API}/admin/employees`),
        axios.get(`${API}/admin/pending-invites`),
        axios.get(`${API}/admin/supervisors`),
      ]);
      setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
      setPendingInvites(Array.isArray(inviteRes.data) ? inviteRes.data : []);
      setSupervisors(Array.isArray(supRes.data) ? supRes.data : []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  // Combine into a unified team list
  const team = useMemo(() => ([
    ...employees.map((e) => ({ ...e, role: 'employee' })),
    ...supervisors.map((s) => ({ ...s, role: 'supervisor' })),
  ]), [employees, supervisors]);

  // KPI counts
  const kpis = useMemo(() => {
    const total = team.length;
    const sup = supervisors.length;
    const onShift = team.filter((m) => m.is_on_shift).length;
    const suspended = team.filter((m) => m.status && m.status !== 'active').length;
    const pending = pendingInvites.length;
    return { total, sup, onShift, suspended, pending };
  }, [team, supervisors.length, pendingInvites.length]);

  // Filter + search
  const baseList = useMemo(() => {
    let list = team;
    switch (filter) {
      case 'employees':  list = list.filter((m) => m.role === 'employee'); break;
      case 'supervisors': list = list.filter((m) => m.role === 'supervisor'); break;
      case 'active':     list = list.filter((m) => m.status === 'active'); break;
      case 'on_shift':   list = list.filter((m) => m.is_on_shift); break;
      case 'off_duty':   list = list.filter((m) => !m.is_on_shift && m.status === 'active'); break;
      case 'suspended':  list = list.filter((m) => m.status && m.status !== 'active'); break;
      default: break;
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      list = list.filter((m) =>
        m.name?.toLowerCase().includes(s) ||
        m.email?.toLowerCase().includes(s) ||
        (m.employee_code || '').includes(s)
      );
    }
    return list;
  }, [team, filter, searchTerm]);

  // First letter of the first name, diacritics-insensitive (Ș→S, Ă→A)
  const firstLetterOf = (name) => {
    const ch = (name || '').trim().charAt(0);
    if (!ch) return '';
    return ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  };

  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const letterCounts = useMemo(() => {
    const counts = Object.fromEntries(ALPHABET.map((l) => [l, 0]));
    for (const m of baseList) {
      const l = firstLetterOf(m.name);
      if (counts[l] !== undefined) counts[l] += 1;
    }
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseList]);

  const visibleTeam = useMemo(() => {
    const list = letterFilter
      ? baseList.filter((m) => firstLetterOf(m.name) === letterFilter)
      : baseList;
    // Sort the visible list alphabetically by full name (case-insensitive,
    // locale-aware so diacritics like Ș/Ț sort naturally). Always returns
    // a NEW array so we never mutate the cached `team`.
    return [...list].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseList, letterFilter]);

  // ---------- Actions ----------
  const handleInvite = async (e, isSupervisor) => {
    e.preventDefault();
    const form = isSupervisor ? supervisorInviteForm : inviteForm;
    const cleanEmail = (form.email || '').trim().toLowerCase();
    const cleanName = (form.name || '').trim();
    if (!cleanName || !cleanEmail) { toast.error('Name and email are required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) { toast.error('Please enter a valid email'); return; }
    setInviting(true);
    try {
      const endpoint = isSupervisor ? '/admin/invite-supervisor' : '/admin/invite';
      const payload = { name: cleanName, email: cleanEmail };
      if (form.position) payload.position = form.position;
      const response = await axios.post(`${API}${endpoint}`, payload);
      if (response.data.invite_token) {
        setInviteLinkDialog({
          open: true,
          link: `${FRONTEND_URL}/register?token=${response.data.invite_token}`,
          name: cleanName,
        });
      } else {
        toast.success('Invitation sent!');
      }
      if (isSupervisor) {
        setSupervisorInviteOpen(false);
        setSupervisorInviteForm({ email: '', name: '', position: '' });
      } else {
        setInviteOpen(false);
        setInviteForm({ email: '', name: '', position: '' });
      }
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to send invitation'));
    } finally {
      setInviting(false);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLinkDialog.link);
    toast.success('Link copied — share via WhatsApp, SMS, or email.');
  };

  const handleDelete = async () => {
    const m = deleteDialog.member;
    if (!m) return;
    try {
      const url = m.role === 'supervisor'
        ? `${API}/admin/supervisors/${m.id}`
        : `${API}/admin/employees/${m.id}`;
      await axios.delete(url);
      toast.success(`${m.name} removed`);
      setDeleteDialog({ open: false, member: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleChangeRole = async () => {
    const m = roleDialog.member;
    if (!m) return;
    const newRole = m.role === 'supervisor' ? 'employee' : 'supervisor';
    try {
      const res = await axios.put(`${API}/admin/users/${m.id}/account-role`, { role: newRole });
      toast.success(res.data.message);
      setRoleDialog({ open: false, member: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleToggleStatus = async (m) => {
    try {
      const res = await axios.put(`${API}/admin/employees/${m.id}/status`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleResetPassword = async (m) => {
    try {
      const res = await axios.post(`${API}/admin/employees/${m.id}/reset-password`);
      toast.success(`Temp password for ${m.name}: ${res.data.temp_password}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleRegenerateCode = async (m) => {
    try {
      const res = await axios.post(`${API}/admin/employees/${m.id}/regenerate-code`);
      toast.success(`New code: ${res.data.new_code}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate code');
    }
  };

  const handleExportStaff = () => {
    if (!team.length) { toast.error('No staff to export'); return; }
    const rows = [
      ['Name', 'Email', 'Role', 'Status', 'On Shift', 'Employee Code', 'Last Clock Event'],
      ...team.map((m) => [
        m.name || '',
        m.email || '',
        m.role || '',
        m.status || '',
        m.is_on_shift ? 'Yes' : 'No',
        m.employee_code || '',
        m.last_clock_event || '',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `san_security_staff_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Staff CSV downloaded');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  const filters = [
    { id: 'all',         label: 'All',         count: team.length },
    { id: 'employees',   label: 'Employees',   count: employees.length },
    { id: 'supervisors', label: 'Supervisors', count: supervisors.length },
    { id: 'active',      label: 'Active',      count: team.filter((m) => m.status === 'active').length },
    { id: 'on_shift',    label: 'On Shift',    count: kpis.onShift },
    { id: 'off_duty',    label: 'Off Duty',    count: team.filter((m) => !m.is_on_shift && m.status === 'active').length },
    { id: 'suspended',   label: 'Suspended',   count: kpis.suspended },
  ];

  return (
    <AdminLayout>
      <div className="p-3 sm:p-6 space-y-5 sm:space-y-6 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-4 sm:pb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={1.5} />
              EMPLOYEES
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Manage your security team</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => setInviteOpen(true)}
              data-testid="invite-employee-btn"
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider text-xs"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Invite Employee
            </Button>
            <Button
              onClick={() => setSupervisorInviteOpen(true)}
              data-testid="invite-supervisor-btn"
              variant="outline"
              className="border-blue-700 text-blue-400 hover:bg-blue-900/20 rounded-sm uppercase tracking-wider text-xs"
            >
              <Shield className="w-4 h-4 mr-2" /> Add Supervisor
            </Button>
            <Button
              onClick={handleExportStaff}
              data-testid="export-staff-btn"
              variant="outline"
              className="border-border hover:bg-accent rounded-sm uppercase tracking-wider text-xs"
            >
              <Download className="w-4 h-4 mr-2" /> Export Staff
            </Button>
          </div>
        </div>

        {/* KPI Grid — 2 per row on mobile */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4" data-testid="employees-kpi-grid">
          <KpiCard title="Total Employees"     value={kpis.total}     icon={Users}      tone="blue"   testId="kpi-total-employees" />
          <KpiCard title="Supervisors"         value={kpis.sup}       icon={Shield}     tone="primary" testId="kpi-supervisors" />
          <KpiCard title="On Shift Now"        value={kpis.onShift}   icon={CheckCircle} tone="green"  testId="kpi-on-shift-now" />
          <KpiCard title="Suspended"           value={kpis.suspended} icon={ShieldOff}  tone="red"    testId="kpi-suspended" />
          <KpiCard title="Pending Invitations" value={kpis.pending}   icon={Mail}       tone="amber"  testId="kpi-pending" />
        </div>

        {/* Pending Invitations — collapsible */}
        {pendingInvites.length > 0 && (
          <Card className="bg-card border-border rounded-sm" data-testid="pending-invites-section">
            <button
              type="button"
              onClick={() => setInvitesOpen((o) => !o)}
              data-testid="pending-invites-toggle"
              className="w-full flex items-center justify-between p-4 hover:bg-accent/30 transition-colors"
            >
              <div className="flex items-center gap-2 text-sm font-barlow tracking-wider uppercase">
                <Mail className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
                Pending Invitations
                <Badge className="ml-2 bg-amber-500/15 text-amber-500 border border-amber-500/40">
                  {pendingInvites.length}
                </Badge>
              </div>
              {invitesOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>
            {invitesOpen && (
              <div className="border-t border-border divide-y divide-border">
                {pendingInvites.map((invite) => (
                  <div key={invite.id} className="flex items-center justify-between p-3 sm:p-4 hover:bg-accent/30">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{invite.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{invite.email}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(`${FRONTEND_URL}/register?token=${invite.token}`);
                          toast.success('Invite link copied');
                        }}
                        data-testid={`copy-invite-${invite.id}`}
                        className="border-border rounded-sm text-xs h-8"
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/admin/invites/${invite.id}`);
                            toast.success('Invitation deleted');
                            fetchData();
                          } catch { toast.error('Failed to delete invitation'); }
                        }}
                        data-testid={`delete-invite-${invite.id}`}
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Filters + search */}
        <div className="space-y-3" data-testid="filters-bar">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-background border-border rounded-sm"
              data-testid="search-employees-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-1">
            {filters.map((f) => (
              <FilterChip
                key={f.id}
                label={f.label}
                count={f.count}
                active={filter === f.id}
                onClick={() => setFilter(f.id)}
                testId={`filter-${f.id}`}
              />
            ))}
          </div>

          {/* Alphabet index */}
          <div className="flex flex-wrap gap-1.5" data-testid="alphabet-index">
            <button
              type="button"
              onClick={() => setLetterFilter(null)}
              data-testid="letter-all"
              className={cn(
                'flex flex-col items-center justify-center w-11 h-11 rounded-sm border transition-colors',
                letterFilter === null
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-accent'
              )}
            >
              <span className="text-[10px] font-barlow tracking-wider uppercase leading-none">All</span>
              <span className={cn('font-mono text-[10px] leading-none mt-1', letterFilter === null ? 'text-primary-foreground/80' : 'text-foreground/60')}>
                {baseList.length}
              </span>
            </button>
            {ALPHABET.map((l) => {
              const count = letterCounts[l];
              const active = letterFilter === l;
              return (
                <button
                  key={l}
                  type="button"
                  disabled={count === 0}
                  onClick={() => setLetterFilter(active ? null : l)}
                  data-testid={`letter-${l}`}
                  className={cn(
                    'flex flex-col items-center justify-center w-11 h-11 rounded-sm border transition-colors',
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : count === 0
                        ? 'bg-card/50 text-muted-foreground/30 border-border/50 cursor-not-allowed'
                        : 'bg-card text-foreground border-border hover:text-primary hover:bg-accent'
                  )}
                >
                  <span className="text-sm font-barlow font-semibold leading-none">{l}</span>
                  <span className={cn('font-mono text-[10px] leading-none mt-1', active ? 'text-primary-foreground/80' : 'text-foreground/50')}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Team list — compact cards */}
        <div className="space-y-2" data-testid="team-list">
          {visibleTeam.length === 0 ? (
            <Card className="bg-card border-border rounded-sm">
              <CardContent className="p-8 text-center text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No staff matching the current filter.</p>
              </CardContent>
            </Card>
          ) : (
            visibleTeam.map((m) => (
              <EmployeeRow
                key={m.id}
                member={m}
                onProfile={() => navigate(`/admin/employees/${m.id}`)}
                onSchedule={() => navigate(`/admin/employees/${m.id}?tab=schedule`)}
                onClockHistory={() => navigate(`/admin/employees/${m.id}?tab=clock-history`)}
                onDocuments={() => navigate(`/admin/employees/${m.id}?tab=documents`)}
                onToggleStatus={() => handleToggleStatus(m)}
                onResetPassword={() => handleResetPassword(m)}
                onRegenerateCode={() => handleRegenerateCode(m)}
                onDelete={() => setDeleteDialog({ open: true, member: m })}
                canChangeRole={isMainAdmin}
                onChangeRole={() => setRoleDialog({ open: true, member: m })}
              />
            ))
          )}
        </div>
      </div>

      {/* Invite Employee Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase">Invite New Employee</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => handleInvite(e, false)} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full Name</Label>
              <Input data-testid="invite-name-input" value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="John Smith" required className="bg-background border-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email Address</Label>
              <Input data-testid="invite-email-input" type="email" value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="john@example.com" required className="bg-background border-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Position</Label>
              <Select value={inviteForm.position} onValueChange={(v) => setInviteForm({ ...inviteForm, position: v })}>
                <SelectTrigger data-testid="invite-position-select" className="bg-background border-border rounded-sm">
                  <SelectValue placeholder="Select position (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {POSITION_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p} data-testid={`invite-position-${p.replace(/\W+/g, '-')}`}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">You can change this any time from the employee&apos;s profile.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} className="border-border rounded-sm">Cancel</Button>
              <Button type="submit" disabled={inviting} data-testid="send-invite-btn"
                className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
                {inviting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Supervisor Dialog */}
      <Dialog open={supervisorInviteOpen} onOpenChange={setSupervisorInviteOpen}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase">Add New Supervisor</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => handleInvite(e, true)} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full Name</Label>
              <Input data-testid="supervisor-name-input" value={supervisorInviteForm.name}
                onChange={(e) => setSupervisorInviteForm({ ...supervisorInviteForm, name: e.target.value })}
                placeholder="Jane Doe" required className="bg-background border-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email Address</Label>
              <Input data-testid="supervisor-email-input" type="email" value={supervisorInviteForm.email}
                onChange={(e) => setSupervisorInviteForm({ ...supervisorInviteForm, email: e.target.value })}
                placeholder="supervisor@example.com" required className="bg-background border-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Position</Label>
              <Select value={supervisorInviteForm.position} onValueChange={(v) => setSupervisorInviteForm({ ...supervisorInviteForm, position: v })}>
                <SelectTrigger data-testid="supervisor-position-select" className="bg-background border-border rounded-sm">
                  <SelectValue placeholder="Select position (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {POSITION_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              Supervisors can clock employees in/out using their codes and manage shifts.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSupervisorInviteOpen(false)} className="border-border rounded-sm">Cancel</Button>
              <Button type="submit" disabled={inviting} data-testid="send-supervisor-invite-btn"
                className="bg-blue-600 text-foreground hover:bg-blue-500 rounded-sm">
                {inviting ? 'Creating...' : 'Create Invitation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Link share dialog */}
      <Dialog open={inviteLinkDialog.open} onOpenChange={(open) => setInviteLinkDialog({ ...inviteLinkDialog, open })}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-primary" /> Share Invite Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Send this link to <span className="text-foreground font-medium">{inviteLinkDialog.name}</span> via WhatsApp, SMS, or email:
            </p>
            <div className="bg-background border border-border rounded-sm p-3">
              <p className="text-xs font-mono break-all">{inviteLinkDialog.link}</p>
            </div>
            <Button onClick={copyInviteLink} data-testid="copy-link-btn"
              className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
              <Copy className="w-4 h-4 mr-2" /> Copy Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow tracking-wider uppercase">
              Remove {deleteDialog.member?.role === 'supervisor' ? 'Supervisor' : 'Employee'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently remove {deleteDialog.member?.name}?
              <span className="block mt-2 text-red-400 font-semibold">
                PERMANENT — all their personal data (profile, onboarding details, documents, bank details, application form) is deleted from every database and CANNOT be recovered.
              </span>
              <span className="block mt-1 text-muted-foreground">
                Work history added by admin (worked/missed shifts, clock history, payslips, holiday records, audit) is kept. Their email address becomes immediately reusable if you re-hire them.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
              className="bg-destructive text-destructive-foreground hover:bg-red-600 rounded-sm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Promote / demote secondary admin confirmation */}
      <AlertDialog open={roleDialog.open} onOpenChange={(open) => setRoleDialog({ ...roleDialog, open })}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow tracking-wider uppercase">
              {roleDialog.member?.role === 'supervisor' ? 'Revert to Employee' : 'Make Secondary Admin'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {roleDialog.member?.role === 'supervisor'
                ? `${roleDialog.member?.name} will lose admin access and return to the employee portal at their next login or page refresh. Same account, same password.`
                : `${roleDialog.member?.name} will get the Secondary Admin portal — full admin menu, but no deletions and no emails. The switch happens at their next login or page refresh. Same account, same password.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleChangeRole}
              data-testid="confirm-change-role-btn"
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
            >
              {roleDialog.member?.role === 'supervisor' ? 'Revert' : 'Promote'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

// ---------- Employee row card ----------
function EmployeeRow({
  member, onProfile, onSchedule, onClockHistory, onDocuments,
  onToggleStatus, onResetPassword, onRegenerateCode, onDelete,
  canChangeRole, onChangeRole,
}) {
  const isActive = member.status === 'active';
  const isSupervisor = member.role === 'supervisor';

  return (
    <Card
      className="bg-card border-border rounded-sm hover:border-primary/40 transition-colors"
      data-testid={`member-${member.id}`}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <button
            type="button"
            onClick={onProfile}
            className={cn(
              'w-10 h-10 sm:w-12 sm:h-12 rounded-sm flex items-center justify-center text-sm font-medium flex-shrink-0 cursor-pointer transition-transform hover:scale-105',
              member.is_on_shift ? 'bg-green-400/10 text-green-400'
                : isSupervisor ? 'bg-blue-400/10 text-blue-400'
                : 'bg-accent text-muted-foreground'
            )}
            data-testid={`avatar-${member.id}`}
          >
            {initialsOf(member.name)}
          </button>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <button
              type="button"
              onClick={onProfile}
              className="text-left w-full"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm sm:text-base font-medium text-foreground truncate">{member.name}</p>
                <ComplianceIndicator userId={member.id} />
                <Badge className={cn(
                  'text-[10px] uppercase tracking-wider',
                  isSupervisor ? 'bg-blue-600/10 text-blue-400 border-blue-900' : 'bg-muted text-muted-foreground border-border'
                )}>
                  {isSupervisor ? <><Shield className="w-3 h-3 mr-1" /> Supervisor</> : 'Employee'}
                </Badge>
                {member.is_on_shift ? (
                  <Badge className="status-active text-[10px] uppercase tracking-wider">
                    <CheckCircle className="w-3 h-3 mr-1" /> On Shift
                  </Badge>
                ) : isActive ? (
                  <Badge className="bg-muted text-muted-foreground border-border text-[10px] uppercase tracking-wider">
                    Off Duty
                  </Badge>
                ) : (
                  <Badge className="bg-red-500/15 text-red-500 border-red-500/40 text-[10px] uppercase tracking-wider">
                    <ShieldOff className="w-3 h-3 mr-1" /> Suspended
                  </Badge>
                )}
              </div>
              <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                {member.employee_code && (
                  <span className="inline-flex items-center gap-1 font-mono">
                    <Hash className="w-3 h-3" /> {member.employee_code}
                  </span>
                )}
                {member.position && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary border border-primary/30 font-medium"
                    data-testid={`position-${member.id}`}
                    title="Staff Position"
                  >
                    <Briefcase className="w-3 h-3" /> {member.position}
                  </span>
                )}
                {member.last_clock_event && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Last:{' '}
                    {(() => { try { return format(parseISO(member.last_clock_event), 'MMM d, h:mm a'); } catch { return '—'; } })()}
                  </span>
                )}
              </div>
            </button>
          </div>

          {/* More menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost" size="icon"
                className="h-8 w-8 flex-shrink-0"
                data-testid={`more-${member.id}`}
                title="More"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-border rounded-sm">
              <DropdownMenuItem onClick={onToggleStatus} data-testid={`toggle-status-${member.id}`}>
                {isActive ? (
                  <><ShieldOff className="w-4 h-4 mr-2 text-red-400" /> Suspend</>
                ) : (
                  <><ShieldCheck className="w-4 h-4 mr-2 text-green-400" /> Reactivate</>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onResetPassword} data-testid={`reset-pw-${member.id}`}>
                <KeyRound className="w-4 h-4 mr-2 text-amber-400" /> Reset Password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onRegenerateCode} data-testid={`regen-code-${member.id}`}>
                <RefreshCw className="w-4 h-4 mr-2 text-blue-400" /> Regenerate Code
              </DropdownMenuItem>
              {canChangeRole && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onChangeRole} data-testid={`change-role-${member.id}`}>
                    {isSupervisor ? (
                      <><UserCog className="w-4 h-4 mr-2 text-orange-400" /> Revert to Employee</>
                    ) : (
                      <><Shield className="w-4 h-4 mr-2 text-blue-400" /> Make Secondary Admin</>
                    )}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} data-testid={`delete-${member.id}`}>
                <Trash2 className="w-4 h-4 mr-2 text-red-400" /> Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Quick actions row */}
        <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-4 gap-1.5">
          <QuickAction label="Profile"  icon={Eye}        onClick={onProfile}      testId={`qa-profile-${member.id}`} />
          <QuickAction label="Schedule" icon={Calendar}   onClick={onSchedule}     testId={`qa-schedule-${member.id}`} />
          <QuickAction label="History"  icon={Clock}      onClick={onClockHistory} testId={`qa-history-${member.id}`} />
          <QuickAction label="Docs"     icon={ListChecks} onClick={onDocuments}    testId={`qa-docs-${member.id}`} />
        </div>
      </CardContent>
    </Card>
  );
}

function QuickAction({ label, icon: Icon, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex flex-col items-center justify-center gap-1 px-2 py-1.5 rounded-sm hover:bg-accent text-xs text-muted-foreground hover:text-foreground transition-colors"
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="uppercase tracking-wider text-[10px]">{label}</span>
    </button>
  );
}
